import React from "react";
import { Field } from "redux-form";
import { TextField } from "redux-form-material-ui";
import { isRequired, isNumeric } from "js/helpers/validations";
import { _t } from "js/utils/TranslationService";
import {
  normalizeFloat,
  formatFloat,
  intParser
} from "js/pages/Admin/Programa/Edit/helper";
import { styles } from "../styles.js";

// Función de validación para máximo de decimales
const maxDecimals = maxDecimals => value => {
  if (!value) return undefined;

  const val = value.toString().trim();
  if (!val) return undefined;

  // Encontrar el separador decimal (coma o punto)
  const lastCommaIndex = val.lastIndexOf(",");
  const lastDotIndex = val.lastIndexOf(".");
  const lastSeparatorIndex = Math.max(lastCommaIndex, lastDotIndex);

  if (lastSeparatorIndex >= 0) {
    // Hay un separador, verificar cuántos dígitos hay después
    const afterSeparator = val
      .slice(lastSeparatorIndex + 1)
      .replace(/[^0-9]/g, "");

    // Si hay más de 2 dígitos después del separador, podría ser miles
    // Verificar si realmente es decimal (1-2 dígitos) o miles (más de 2)
    if (afterSeparator.length <= 2) {
      // Es separador decimal
      if (afterSeparator.length > maxDecimals) {
        return `Solo se permiten ${maxDecimals} decimal${
          maxDecimals !== 1 ? "es" : ""
        }`;
      }
    } else {
      // Podría ser miles, buscar el separador decimal real
      // Si hay coma, esa es el separador decimal
      if (lastCommaIndex >= 0) {
        const afterComma = val.slice(lastCommaIndex + 1).replace(/[^0-9]/g, "");
        if (afterComma.length > maxDecimals) {
          return `Solo se permiten ${maxDecimals} decimal${
            maxDecimals !== 1 ? "es" : ""
          }`;
        }
      }
    }
  }

  return undefined;
};

// Añade estas utilidades arriba del componente
const isNavigationKey = e => {
  const nav = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End"
  ];
  return nav.includes(e.key) || e.ctrlKey || e.metaKey; // permite Ctrl/⌘ + A,C,V,X,Z
};

const handleIntegerKeyDown = e => {
  if (isNavigationKey(e)) return;
  if (e.key >= "0" && e.key <= "9") return;
  e.preventDefault();
};

const handleDecimalKeyDown = decimalNumbers => e => {
  if (isNavigationKey(e)) return;

  const key = e.key;
  const { value = "", selectionStart = 0, selectionEnd = 0 } = e.target;
  const selection = value.slice(selectionStart, selectionEnd);

  // Permitir números siempre
  if (key >= "0" && key <= "9") {
    // Verificar si estamos escribiendo después del separador decimal
    const lastCommaIndex = value.lastIndexOf(",");
    const lastDotIndex = value.lastIndexOf(".");
    const lastSeparatorIndex = Math.max(lastCommaIndex, lastDotIndex);

    if (lastSeparatorIndex >= 0) {
      // Hay un separador, verificar cuántos dígitos hay después
      const textAfterSeparator = value.slice(lastSeparatorIndex + 1);
      const digitsAfterSeparator = textAfterSeparator.replace(/[^0-9]/g, "");

      // Si hay 1-2 dígitos después del separador, es separador decimal
      if (digitsAfterSeparator.length <= 2) {
        // Verificar si ya alcanzamos el máximo de decimales
        if (digitsAfterSeparator.length >= decimalNumbers) {
          // Ya tenemos el máximo, bloquear más dígitos a menos que se esté reemplazando
          const textBeforeCursor = value.slice(0, selectionStart);
          const digitsBeforeCursorInDecimal = textBeforeCursor
            .slice(lastSeparatorIndex + 1)
            .replace(/[^0-9]/g, "").length;

          // Si el cursor está después del separador y ya hay max decimales, bloquear
          if (
            selectionStart > lastSeparatorIndex &&
            digitsBeforeCursorInDecimal >= decimalNumbers &&
            selection.length === 0
          ) {
            e.preventDefault();
            return;
          }
        }
      }
    }
    return;
  }

  // Controlar separadores decimales (coma o punto)
  if (key === "," || key === ".") {
    // Verificar si hay dígitos antes de la posición de inserción
    const textBeforeCursor = value.slice(0, selectionStart);
    const digitsBeforeCursor = textBeforeCursor.replace(/[^0-9]/g, "");
    const digitsInSelection = selection.replace(/[^0-9]/g, "");

    // No permitir separador si no hay dígitos antes (ni en la selección)
    if (digitsBeforeCursor.length === 0 && digitsInSelection.length === 0) {
      e.preventDefault();
      return;
    }

    // Encontrar el último separador (punto o coma) en el valor completo
    const lastCommaIndex = value.lastIndexOf(",");
    const lastDotIndex = value.lastIndexOf(".");
    const lastSeparatorIndex = Math.max(lastCommaIndex, lastDotIndex);

    // Verificar si ya existe un separador decimal
    if (lastSeparatorIndex >= 0) {
      const afterLastSeparator = value
        .slice(lastSeparatorIndex + 1)
        .replace(/[^0-9]/g, "");

      // Si hay dígitos después del último separador, verificar cuántos
      if (afterLastSeparator.length > 0) {
        // Si hay 1-2 dígitos después del separador, es un separador decimal
        // Si hay más de 2 dígitos, es un separador de miles
        if (afterLastSeparator.length <= 2) {
          // Es un separador decimal, no permitir otro a menos que se esté reemplazando
          const selectionContainsSeparator =
            selection.includes(".") || selection.includes(",");
          if (!selectionContainsSeparator) {
            e.preventDefault();
            return;
          }
        }
        // Si hay más de 2 dígitos, es separador de miles, permitir agregar separador decimal
      } else {
        // No hay dígitos después del separador, podría ser que el usuario está escribiendo
        // Verificar si el separador está cerca del final (dentro de los últimos 3 caracteres)
        const distanceFromEnd = value.length - lastSeparatorIndex;
        if (distanceFromEnd <= 3) {
          // El separador está cerca del final, probablemente es decimal en proceso
          const selectionContainsSeparator =
            selection.includes(".") || selection.includes(",");
          if (!selectionContainsSeparator) {
            e.preventDefault();
            return;
          }
        }
        // Si está más lejos del final, es de miles, permitir
      }
    }

    return; // Permitir el separador
  }

  // Bloquear cualquier otra tecla
  e.preventDefault();
};

const InputNumberField = ({
  name,
  label,
  placeholder,
  required,
  classes,
  disabled,
  translation,
  customValidations = [],
  InputProps,
  helperText,
  isFloat = false,
  decimalNumbers = 2,
  variant
}) => (
  <Field
    type="text"
    required={required}
    name={name}
    label={translation || _t(label)}
    placeholder={placeholder}
    component={TextField}
    disabled={disabled}
    fullWidth
    format={formatFloat(isFloat ? decimalNumbers : 0)}
    normalize={normalizeFloat(isFloat ? decimalNumbers : 0)}
    parse={!isFloat && intParser}
    validate={(() => {
      let validations = [];
      if (required) {
        validations.push(isRequired);
      }
      validations.push(isNumeric);
      if (isFloat) {
        validations.push(maxDecimals(decimalNumbers));
      }
      return validations.concat(customValidations);
    })()}
    className={classes ? classes.field : styles.field}
    InputProps={InputProps}
    inputProps={{
      pattern: isFloat ? "[0-9.,]*" : "[0-9]*",
      inputMode: isFloat ? "decimal" : "numeric"
    }}
    onKeyDown={
      isFloat
        ? handleDecimalKeyDown(isFloat ? decimalNumbers : 0)
        : handleIntegerKeyDown
    }
    InputLabelProps={{ shrink: true }}
    helperText={helperText}
    variant={variant}
  />
);

export default InputNumberField;
